/**
 * The class constants every bottom analysis dock is drawn with (spec §B0.3).
 *
 * The audit counted five control heights (h-6, h-7, h-8, `size="sm"`'s h-8 and
 * a checkbox's own 16 px) and four text sizes in one dock header, typed by hand
 * into each control. Nobody chose that; it accreted. So the dock has exactly
 * ONE control size and THREE text sizes, and they are spelled here so nobody
 * types a height again — a control that needs to be a different size is a
 * control that should not be in the dock chrome.
 *
 *  - Controls are `h-7` (28 px) at `text-xs` — the tab-bar tools, the header's
 *    panel buttons, the steppers, the menus' triggers.
 *  - Text is 11 px (labels, meta, captions), 12 px (body and controls) and
 *    16 px (KPI values), plus `sv-sta` mono for stations (from `SV_TOKENS`).
 *
 * The `sv-*` palette classes stay where they are (`SV_TOKENS`): those are
 * unlayered CSS and win over Tailwind, which is the reason they exist. These
 * are plain Tailwind class strings, safe to `cn()` together.
 */

/** The focus ring every dock control draws — one ring, one colour. */
const FOCUS = 'focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none';

/** The muted ink of a control that is not the current choice. */
const MUTED_INK = 'text-slate-500 dark:text-stone-400';

export const DOCK_CONTROL = {
  /** The focus ring, for a control that composes its own classes. */
  focus: FOCUS,
  /** The one control height and text size, for anything not covered below. */
  size: 'h-7 text-xs',
  /**
   * A bordered text button — a menu trigger (View ▾, Export ▾), a link-like
   * action in the tab bar. Outline, because the tab bar is a row of tools on
   * the dock's own surface and a filled button there reads as the page's
   * primary action.
   */
  button: `inline-flex h-7 shrink-0 items-center gap-1 rounded-md border border-slate-300 px-2 text-xs whitespace-nowrap hover:bg-slate-200/70 aria-expanded:bg-slate-200/70 dark:border-stone-600 dark:hover:bg-stone-700/70 dark:aria-expanded:bg-stone-700/70 ${FOCUS}`,
  /**
   * The ON look of a `button` used as a toggle (`aria-pressed`): the
   * clearance cross-section's "Violations only", the strip map's "Auto".
   * Compose it after `button`; inert while the control is not pressed.
   */
  pressed:
    'aria-pressed:border-slate-500 aria-pressed:bg-slate-200 aria-pressed:font-semibold aria-pressed:text-slate-900 dark:aria-pressed:border-stone-400 dark:aria-pressed:bg-stone-700 dark:aria-pressed:text-white',
  /**
   * A square icon button: the header's ⓘ ⤢ ✕, the ⋯ overflow. `aria-pressed`
   * styles a toggle that is on and is inert on a plain button.
   */
  iconButton: `inline-flex size-7 shrink-0 items-center justify-center rounded-md ${MUTED_INK} hover:bg-slate-200/70 hover:text-slate-900 aria-expanded:bg-slate-200/70 aria-expanded:text-slate-900 aria-pressed:bg-slate-200/70 aria-pressed:text-slate-900 dark:hover:bg-stone-700/70 dark:hover:text-white dark:aria-expanded:bg-stone-700/70 dark:aria-expanded:text-white dark:aria-pressed:bg-stone-700/70 dark:aria-pressed:text-white ${FOCUS}`,
  /**
   * What a control that cannot act right now looks like — a DATA condition
   * (nothing to step to) or a locked tier alike. Always paired with
   * `aria-disabled`, never with the HTML `disabled` attribute: that takes the
   * control out of hover and the tab order, and a locked one would swallow the
   * click that opens the upgrade dialog (CLAUDE.md).
   */
  inert: 'opacity-50',
  /** The icon size inside a dock control. */
  icon: 'size-4',
  /** The icon size inside a text button (a menu chevron). */
  iconSm: 'size-3.5',
} as const;

export const DOCK_TEXT = {
  /** 11 px — KPI labels, captions, menu section headings, legend items. */
  meta: 'text-[11px] leading-4',
  /** 11 px in the muted ink — the same rung, for secondary copy. */
  metaMuted: `text-[11px] leading-4 ${MUTED_INK}`,
  /** 12 px — body copy and controls (the dock root sets it once). */
  body: 'text-xs',
  /** 16 px — a KPI value, the only large figure in the dock. */
  kpi: 'text-base leading-5 font-semibold',
  /** The muted ink alone, for composing. */
  muted: MUTED_INK,
} as const;
